import { useCallback, useEffect, useRef, useState } from "react";
import SectionHeading from "./SectionHeading";
import { reviews } from "@/mocks/home";

export default function Reviews() {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const cardWidth = el.scrollWidth / reviews.length;
    const index = Math.round(el.scrollLeft / cardWidth);
    setActiveIndex(Math.min(Math.max(index, 0), reviews.length - 1));
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollTo = (dir: -1 | 1) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: "smooth" });
  };

  const scrollToIndex = (index: number) => {
    const el = trackRef.current;
    if (!el) return;
    const cardWidth = el.scrollWidth / reviews.length;
    el.scrollTo({ left: cardWidth * index, behavior: "smooth" });
  };

  return (
    <section id="reviews" className="w-full bg-background-100 py-20 md:py-28">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <SectionHeading
            align="left"
            eyebrow="Customer Review"
            title="고객이 남긴"
            highlight="진짜 후기"
            desc="네이버·카카오 채널로 접수된 실제 수리 후기입니다. 평균 만족도 4.9점을 유지하고 있습니다."
          />
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => scrollTo(-1)}
              aria-label="이전 후기"
              className="flex h-11 w-11 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-800 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-600"
            >
              <i className="ri-arrow-left-line text-xl"></i>
            </button>
            <button
              type="button"
              onClick={() => scrollTo(1)}
              aria-label="다음 후기"
              className="flex h-11 w-11 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-800 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-600"
            >
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          </div>
        </div>

        <div className="reveal mt-12">
          <div
            ref={trackRef}
            className="no-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2"
          >
            {reviews.map((review) => (
              <article
                key={review.id}
                className="flex w-[85%] shrink-0 snap-start flex-col rounded-lg border border-background-200 bg-background-50 p-6 sm:w-[48%] lg:w-[32%]"
              >
                <div className="flex items-center gap-1 text-accent-500">
                  {Array.from({ length: review.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-base"></i>
                  ))}
                </div>
                <p className="mt-4 flex-1 font-body text-[15px] leading-relaxed text-foreground-700">
                  &ldquo;{review.text}&rdquo;
                </p>
                <div className="mt-6 flex items-center gap-3 border-t border-background-200 pt-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 font-heading text-base font-black text-primary-700">
                    {review.name.charAt(0)}
                  </span>
                  <div>
                    <p className="font-label text-sm font-bold text-foreground-950">{review.name}</p>
                    <p className="font-body text-xs text-foreground-500">{review.model}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-center gap-2">
            {reviews.map((review, index) => (
              <button
                key={review.id}
                type="button"
                onClick={() => scrollToIndex(index)}
                aria-label={`${index + 1}번째 후기로 이동`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  activeIndex === index ? "w-7 bg-primary-500" : "w-2 bg-background-400"
                }`}
              ></button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}